import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HelpTooltip } from "@/components/ui/customs/help-tooltip";
import { useGetUsersBo } from "@/api/user.api";
import { useAssignExpeditions } from "@/api/expedition.api";
import type { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";

type SingleProps = {
  expedition: GetAllExpeditionBoResponseDtoExpedition;
  expeditionIds?: never;
};

type BulkProps = {
  expeditionIds: string[];
  expedition?: never;
};

type AssignExpeditionModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
} & (SingleProps | BulkProps);

const ASSIGNABLE_USER_ROLES = ["ADMIN", "MEMBER"];
const NOT_ASSIGNABLE_USERS = ["Antonin"];

export function AssignExpeditionModal({
  expedition,
  expeditionIds,
  open,
  onClose,
  onSuccess,
}: AssignExpeditionModalProps) {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const { data: users } = useGetUsersBo({ isDeleted: false });
  const { mutate: assignExpeditions, isPending } = useAssignExpeditions();

  const isBulk = !!expeditionIds;
  const ids = isBulk ? expeditionIds : [expedition.id];

  const currentAssignedTo = !isBulk
    ? ((expedition as any).assignedTo as
        | { id: string; firstName: string; lastName: string }
        | null
        | undefined)
    : undefined;

  const [selectedUserId, setSelectedUserId] = useState<string>(currentAssignedTo?.id ?? "none");
  const [notify, setNotify] = useState<boolean>(false);

  const handleAssign = () => {
    const userId = selectedUserId === "none" ? undefined : selectedUserId;
    assignExpeditions(
      {
        expeditionIds: ids,
        userId,
        notify: notify && !!userId,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onClose();
        },
      },
    );
  };

  const title = isBulk ? t("assignModal.bulkTitle", { count: ids.length }) : t("assignModal.title");

  const description = isBulk
    ? t("assignModal.bulkDescription", { count: ids.length })
    : t("assignModal.description");

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <p className="text-sm text-gray-500">{description}</p>
          <Select
            value={selectedUserId}
            onValueChange={(value) => {
              setSelectedUserId(value);
              if (value === "none") setNotify(false);
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("assignModal.userPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {users
                ?.filter(
                  (u) =>
                    ASSIGNABLE_USER_ROLES.includes(u.role) &&
                    u.isDeleted === false &&
                    !NOT_ASSIGNABLE_USERS.includes(u.firstName),
                )
                .map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Checkbox
              id="notify-assignee"
              checked={notify}
              onCheckedChange={(checked) => setNotify(checked === true)}
              disabled={selectedUserId === "none"}
            />
            <HelpTooltip content={t("assignModal.notifyHelp")}>
              <Label htmlFor="notify-assignee" className="cursor-pointer">
                {t("assignModal.notifyLabel")}
              </Label>
            </HelpTooltip>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common:actions.cancel")}
          </Button>
          <Button onClick={handleAssign} disabled={isPending}>
            {t("assignModal.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
